import { useState, useRef, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { B, FONT, fmtDate } from '../../lib/constants';
import { notifyProjectClients, sendPushNotification } from '../../hooks/usePushNotifications';
import { uploadToDrive } from '../../lib/google';

const CATEGORIES = [
  'Exterior', 'Interior', 'Foundation', 'Concrete', 'Framing', 'Fireplaces', 'Windows/Doors',
  'Plumbing', 'Electrical', 'HVAC', 'Tile', 'Flooring', 'Trim', 'Paint/Stain', 'Appliances', 'Finishes',
];

const DECISION_PHOTO_FOLDERS = {
  1: '156CZCv45M_MRtgCMJX_Vq5DcGJJd6zuX',
  2: '1WbL75O52-ea50nLiwJuNUPjVq-s5Ufn6',
  3: '1CFJOP_rfY47wEEXlvPjq8S7opiYWAS-x',
};

export default function DecisionDetailModal({ decision, onClose, onSuccess, onDelete, isBuilder = true }) {
  const [answer, setAnswer] = useState(decision.answer || '');
  const [selectedOption, setSelectedOption] = useState(decision.answer || '');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [notifySent, setNotifySent] = useState(false);
  const [editing, setEditing] = useState(false);
  const [comments, setComments] = useState([]);
  const [myName, setMyName] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: prof } = await supabase.from('user_profiles').select('*').eq('id', user.id).single();
        const nm = prof?.name || prof?.full_name || user.email?.split('@')[0] || 'Client';
        setMyName(nm);
      } catch(e) { setMyName('Client'); }
    })();
  }, []);
  const [commentText, setCommentText] = useState('');
  const [sendingComment, setSendingComment] = useState(false);

  const loadComments = async () => {
    const { data } = await supabase.from('decision_comments').select('*')
      .eq('decision_id', decision.id).order('created_at', { ascending: true });
    if (data) setComments(data);
  };
  useEffect(() => { loadComments(); }, [decision.id]);

  const sendComment = async () => {
    if (!commentText.trim()) return;
    setSendingComment(true);
    await supabase.from('decision_comments').insert({
      decision_id: decision.id,
      author_role: isBuilder ? 'builder' : 'client',
      content: commentText.trim(),
    });
    try {
      const url = `${window.location.origin}?project=${decision.project_id}&tab=decisions`;
      if (isBuilder) {
        const { notifyProjectClients } = await import('../../hooks/usePushNotifications');
        await notifyProjectClients(decision.project_id, `💬 Dan commented — ${decision.item}`, commentText.trim().slice(0, 100), url);
      } else {
        const { notifyBuilder } = await import('../../hooks/usePushNotifications');
        await notifyBuilder(`💬 Client commented — ${decision.item}`, commentText.trim().slice(0, 100), url);
      }
    } catch(e) {}
    setCommentText('');
    setSendingComment(false);
    loadComments();
  };
  const [editItem, setEditItem] = useState(decision.item || '');
  const [editAnswer, setEditAnswer] = useState(decision.answer || '');
  const [editCategory, setEditCategory] = useState(decision.category || '');
  const [editNotes, setEditNotes] = useState(decision.notes || '');
  const [editInternalNotes, setEditInternalNotes] = useState(decision.internal_notes || '');
  const [editDueDate, setEditDueDate] = useState(decision.due_date || '');
  const [needMoreInfo, setNeedMoreInfo] = useState(false);
  const [moreInfoText, setMoreInfoText] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const photoRef = useRef();

  const [parentQ, setParentQ] = useState(null);
  useEffect(() => {
    if (!decision.parent_decision_id) { setParentQ(null); return; }
    supabase.from('decisions').select('item, answer').eq('id', decision.parent_decision_id).single()
      .then(({ data }) => { if (data) setParentQ(data); });
  }, [decision.id]);

  const isOptions = decision.answer_type === 'options';
  const options = decision.options || [];
  const isCompleted = decision.status === 'approved';
  const isAwaitingConfirm = decision.status === 'pending_confirmation';

  const handlePhotoSelect = (files) => {
    const newPhotos = Array.from(files).map(f => ({ file: f, preview: URL.createObjectURL(f) }));
    setPhotos(prev => [...prev, ...newPhotos]);
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) setPhotos(prev => [...prev, { file, preview: URL.createObjectURL(file) }]);
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length) handlePhotoSelect(files);
  };

  const uploadPhotos = async () => {
    const token = localStorage.getItem('google_access_token');
    const folderId = DECISION_PHOTO_FOLDERS[parseInt(decision.project_id)];
    if (!token || !folderId || photos.length === 0) return null;
    setUploadingPhoto(true);
    const uploaded = [];
    for (const p of photos) {
      try {
        const result = await uploadToDrive(p.file, folderId, `decision_${decision.id}_${Date.now()}.jpg`);
        uploaded.push({ url: result.webViewLink, id: result.id });
      } catch(e) {}
    }
    setUploadingPhoto(false);
    return uploaded[0] || null;
  };

  const handleReopen = async () => {
    setSaving(true);
    const { error } = await supabase.from('decisions').update({
      status: 'pending',
      answer: null,
      completed_by: null,
      completed_at: null,
      decided_by_name: null,
      confirmed_by_name: null,
      confirmed_at: null,
    }).eq('id', decision.id);
    if (!error) {
      try {
        const { notifyProjectClients } = await import('../../hooks/usePushNotifications');
        await notifyProjectClients(decision.project_id, `🔄 Decision reopened — ${decision.item}`, 'Dan moved this back to Needed. Please take another look.', `${window.location.origin}?project=${decision.project_id}&tab=decisions`);
      } catch(e) {}
    }
    setSaving(false);
    if (!error) { if (onSuccess) onSuccess(); onClose(); }
  };

  const handleConfirm = async () => {
    setSaving(true);
    const { error } = await supabase.from('decisions').update({
      status: 'approved',
      confirmed_by_name: myName || 'Client',
      confirmed_at: new Date().toISOString(),
    }).eq('id', decision.id);
    if (!error) {
      try {
        const { data: builderSubs } = await supabase
          .from('push_subscriptions').select('subscription').in('role', ['builder', 'assistant']);
        const url = `${window.location.origin}?project=${decision.project_id}&tab=decisions`;
        for (const sub of (builderSubs || [])) {
          await sendPushNotification(sub.subscription, `✓ Decision Confirmed — ${decision.item}`, `Confirmed by ${myName || 'Client'}`, url);
        }
      } catch(e) {}
    }
    setSaving(false);
    if (!error) { if (onSuccess) onSuccess(); onClose(); }
  };

  const handleDelete = async () => {
    setSaving(true);
    await supabase.from('decisions').delete().eq('id', decision.id);
    setSaving(false);
    if (onSuccess) onSuccess();
    onClose();
  };

  const handleFinalDecision = async () => {
    const finalAnswer = isOptions ? selectedOption : answer;
    if (!finalAnswer.trim()) return;
    setSaving(true);
    const photo = await uploadPhotos();
    const { error } = await supabase.from('decisions').update({
      status: isBuilder ? 'pending_confirmation' : 'approved',
      answer: finalAnswer.trim(),
      completed_by: isBuilder ? 'builder' : 'client',
      completed_at: new Date().toISOString(),
      decided_by_name: isBuilder ? 'Dan' : (myName || 'Client'),
      ...(isBuilder ? {} : { confirmed_by_name: myName || 'Client', confirmed_at: new Date().toISOString() }),
      ...(photo ? { photo_url: photo.url, photo_drive_id: photo.id } : {}),
    }).eq('id', decision.id);

    if (!error && !isBuilder) {
      const { data: builderSubs } = await supabase
        .from('push_subscriptions').select('subscription').in('role', ['builder', 'assistant']);
      if (builderSubs?.length) {
        const url = `${window.location.origin}?project=${decision.project_id}&tab=decisions`;
        for (const sub of builderSubs) {
          await sendPushNotification(sub.subscription, `Decision Answered: ${decision.item}`, `${finalAnswer.trim().slice(0, 80)} — ${myName || 'Client'}`, url);
        }
      }
    }

    if (!error && isBuilder) {
      // Ask the client(s) to confirm
      try {
        const { notifyProjectClients } = await import('../../hooks/usePushNotifications');
        await notifyProjectClients(decision.project_id, `✋ Please confirm: ${decision.item}`, finalAnswer.trim().slice(0, 100), `${window.location.origin}?project=${decision.project_id}&tab=decisions`);
      } catch(e) {}
    }

    setSaving(false);
    if (!error) { if (onSuccess) onSuccess(); onClose(); }
  };

  const handleNeedMoreInfo = async () => {
    if (!moreInfoText.trim()) return;
    setSaving(true);
    await supabase.from('decisions').update({
      notes: (decision.notes ? decision.notes + '\n\nClient: ' : 'Client: ') + moreInfoText.trim(),
    }).eq('id', decision.id);
    // Notify builder the client needs more info
    try {
      const { data: builderSubs } = await supabase
        .from('push_subscriptions').select('subscription').in('role', ['builder', 'assistant']);
      if (builderSubs?.length) {
        const url = `${window.location.origin}?project=${decision.project_id}&tab=decisions`;
        for (const sub of builderSubs) {
          await sendPushNotification(sub.subscription, `❓ More Info Needed: ${decision.item}`, moreInfoText.trim().slice(0, 100), url);
        }
      }
    } catch(e) {}
    setSaving(false);
    if (onSuccess) onSuccess();
    onClose();
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    await supabase.from('decisions').update({
      item: editItem.trim(),
      answer: editAnswer.trim() || null,
      category: editCategory || null,
      notes: editNotes.trim() || null,
      internal_notes: editInternalNotes.trim() || null,
      due_date: editDueDate || null,
    }).eq('id', decision.id);
    setSaving(false);
    setEditing(false);
    if (onSuccess) onSuccess();
  };

  const handleNotifyClient = async () => {
    setNotifying(true);
    const url = `${window.location.origin}?project=${decision.project_id}&tab=decisions&decision=${decision.id}`;
    await notifyProjectClients(decision.project_id, `Decision: ${decision.item}`, decision.notes?.slice(0, 100) || 'Tap to view and answer.', url);
    setNotifying(false);
    setNotifySent(true);
    setTimeout(() => setNotifySent(false), 3000);
  };

  const inputStyle = { width: '100%', background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 9, padding: '10px 12px', color: B.white, fontSize: 13, outline: 'none', fontFamily: FONT, boxSizing: 'border-box' };
  const labelStyle = { color: B.muted, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6, display: 'block' };



  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', paddingTop: 20, paddingBottom: 20 }}>
      <div style={{ background: B.navy, borderRadius: 20, padding: 24, width: '100%', maxWidth: 540, margin: '0 16px' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div style={{ flex: 1, paddingRight: 12 }}>
            <div style={{ color: isCompleted ? '#4CAF7D' : B.goldlt, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 6 }}>
              {isCompleted ? '✓ Completed' : 'Needs Decision'}
            </div>
            {parentQ && (
              <div style={{ color: '#4FA3E0', fontSize: 12, fontWeight: 700, marginBottom: 3 }}>
                {parentQ.item}{decision.parent_option ? ` · ${decision.parent_option}` : parentQ.answer ? ` · ${parentQ.answer}` : ''}
              </div>
            )}
            <div style={{ color: B.white, fontSize: 18, fontWeight: 800, lineHeight: 1.3 }}>{parentQ ? '↳ ' : ''}{decision.item}</div>
            {decision.category && <div style={{ color: B.gold, fontSize: 11, marginTop: 4 }}>{decision.category}</div>}
            {decision.due_date && !isCompleted && <div style={{ color: B.muted, fontSize: 11, marginTop: 4 }}>Due {fmtDate(decision.due_date)}</div>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {isBuilder && !editing && (
              <button onClick={() => setEditing(true)}
                style={{ background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 7, padding: '5px 10px', cursor: 'pointer', color: B.muted, fontSize: 11, fontWeight: 700, fontFamily: FONT }}>
                Edit
              </button>
            )}
            {isBuilder && !confirmDelete && (
              <button onClick={() => setConfirmDelete(true)}
                style={{ background: 'transparent', border: '1px solid #E74C3C', borderRadius: 7, padding: '5px 10px', cursor: 'pointer', color: '#E74C3C', fontSize: 11, fontWeight: 700, fontFamily: FONT }}>
                Delete
              </button>
            )}
            {isBuilder && confirmDelete && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span style={{ color: B.muted, fontSize: 11 }}>Sure?</span>
                <button onClick={handleDelete} disabled={saving}
                  style={{ background: '#E74C3C', border: 'none', borderRadius: 7, padding: '5px 12px', cursor: 'pointer', color: '#fff', fontSize: 11, fontWeight: 700, fontFamily: FONT }}>
                  {saving ? '…' : 'Yes'}
                </button>
                <button onClick={() => setConfirmDelete(false)}
                  style={{ background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 7, padding: '5px 10px', cursor: 'pointer', color: B.muted, fontSize: 11, fontFamily: FONT }}>
                  No
                </button>
              </div>
            )}
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: B.muted, fontSize: 24, cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>
        </div>

        {/* Client-visible notes */}
        {decision.notes && !editing && (
          <div style={{ background: B.mid, borderRadius: 10, padding: '12px 14px', marginBottom: 16, border: `1px solid ${B.slate}` }}>
            <div style={{ color: B.muted, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 5 }}>Comments</div>
            <div style={{ color: B.light, fontSize: 13, lineHeight: 1.5 }}>{decision.notes}</div>
          </div>
        )}

        {/* Internal notes — builder only */}
        {isBuilder && decision.internal_notes && !editing && (
          <div style={{ background: 'rgba(154,115,47,0.08)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, border: `1px solid ${B.gold}` }}>
            <div style={{ color: B.gold, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 5 }}>🔒 Internal Notes</div>
            <div style={{ color: B.light, fontSize: 13, lineHeight: 1.5 }}>{decision.internal_notes}</div>
          </div>
        )}

        {/* Notify Client button — builder only */}
        {isBuilder && !isCompleted && !editing && (
          <button onClick={handleNotifyClient} disabled={notifying}
            style={{ width: '100%', background: notifySent ? '#4CAF7D' : 'rgba(248,218,107,0.1)', border: `1px solid ${notifySent ? '#4CAF7D' : B.goldlt}`, borderRadius: 10, padding: '11px', cursor: 'pointer', color: notifySent ? '#fff' : B.goldlt, fontSize: 13, fontWeight: 700, fontFamily: FONT, marginBottom: 20 }}>
            {notifying ? 'Sending…' : notifySent ? '✓ Notification Sent!' : '🔔 Notify Client'}
          </button>
        )}

        {/* EDIT MODE */}
        {editing && (
          <div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Decision Name</label>
              <input value={editItem} onChange={e => setEditItem(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Category</label>
              <select value={editCategory} onChange={e => setEditCategory(e.target.value)} style={inputStyle}>
                <option value="">No category</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Answer</label>
              <textarea value={editAnswer} onChange={e => setEditAnswer(e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Client-Visible Notes</label>
              <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>🔒 Internal Notes (only you see this)</label>
              <textarea value={editInternalNotes} onChange={e => setEditInternalNotes(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Date Needed</label>
              <input type="date" value={editDueDate} onChange={e => setEditDueDate(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <button onClick={() => setEditing(false)}
                style={{ background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 12, padding: 13, cursor: 'pointer', color: B.muted, fontSize: 13, fontFamily: FONT }}>
                Cancel
              </button>
              <button onClick={handleSaveEdit} disabled={saving}
                style={{ background: B.goldlt, border: 'none', borderRadius: 12, padding: 13, cursor: 'pointer', color: B.black, fontSize: 13, fontWeight: 800, fontFamily: FONT }}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}

        {/* COMPLETED VIEW */}
        {!editing && isCompleted && (
          <div style={{ background: 'rgba(76,175,61,0.12)', border: '1px solid #4CAF7D', borderRadius: 14, padding: 20 }}>
            <div style={{ color: '#4CAF7D', fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Final Decision</div>
            <div style={{ color: B.white, fontSize: 18, fontWeight: 800, marginBottom: 12, lineHeight: 1.4 }}>{decision.answer}</div>
            {decision.photo_drive_id && (
              <a href={decision.photo_url} target="_blank" rel="noreferrer" style={{ display: 'block', marginBottom: 14 }}>
                <img src={`https://drive.google.com/thumbnail?id=${decision.photo_drive_id}&sz=w800`} alt=""
                  style={{ width: '100%', maxHeight: 280, objectFit: 'cover', borderRadius: 10 }}
                  onError={e => e.target.style.display='none'} />
              </a>
            )}
            <div style={{ display: 'flex', gap: 16 }}>
              <div>
                <div style={{ color: B.muted, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' }}>Completed By</div>
                <div style={{ color: B.light, fontSize: 13, marginTop: 2, textTransform: 'capitalize' }}>{decision.completed_by || 'Builder'}</div>
              </div>
              {decision.completed_at && (
                <div>
                  <div style={{ color: B.muted, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' }}>Date</div>
                  <div style={{ color: B.light, fontSize: 13, marginTop: 2 }}>{fmtDate(decision.completed_at?.split('T')[0])}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* PENDING — Answer input */}
        {!editing && !isCompleted && (
          <>
            {/* Need More Info — client only */}
            {!isBuilder && !needMoreInfo && (
              <button onClick={() => setNeedMoreInfo(true)}
                style={{ width: '100%', background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 10, padding: '10px', cursor: 'pointer', color: B.muted, fontSize: 12, fontWeight: 600, fontFamily: FONT, marginBottom: 12 }}>
                ❓ I Need More Info
              </button>
            )}
            {!isBuilder && needMoreInfo && (
              <div style={{ background: B.mid, borderRadius: 12, padding: 14, marginBottom: 16, border: `1px solid ${B.slate}` }}>
                <div style={{ color: B.muted, fontSize: 11, marginBottom: 8 }}>What do you need to know?</div>
                <textarea value={moreInfoText} onChange={e => setMoreInfoText(e.target.value)} placeholder="What information do you need before deciding?" rows={3}
                  style={{ width: '100%', background: B.navy, border: `1px solid ${B.slate}`, borderRadius: 8, padding: '8px 10px', color: B.white, fontSize: 13, outline: 'none', fontFamily: FONT, boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.5, marginBottom: 8 }} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <button onClick={() => setNeedMoreInfo(false)}
                    style={{ background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 8, padding: '8px', cursor: 'pointer', color: B.muted, fontFamily: FONT, fontSize: 12 }}>Cancel</button>
                  <button onClick={handleNeedMoreInfo} disabled={!moreInfoText.trim()}
                    style={{ background: moreInfoText.trim() ? B.goldlt : B.slate, color: moreInfoText.trim() ? B.black : B.muted, border: 'none', borderRadius: 8, padding: '8px', cursor: 'pointer', fontFamily: FONT, fontSize: 12, fontWeight: 700 }}>Send</button>
                </div>
              </div>
            )}

            {/* Answer input */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: B.muted, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 10 }}>
                {isOptions ? 'Select an Option' : 'Enter Decision'}
              </div>
              {isOptions ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {options.map((opt, i) => (
                    <button key={i} onClick={() => setSelectedOption(opt)}
                      style={{ background: selectedOption === opt ? 'rgba(154,115,47,0.2)' : B.mid, border: `2px solid ${selectedOption === opt ? B.goldlt : B.slate}`, borderRadius: 11, padding: '13px 16px', cursor: 'pointer', color: selectedOption === opt ? B.goldlt : B.white, fontSize: 14, fontWeight: selectedOption === opt ? 700 : 400, fontFamily: FONT, textAlign: 'left' }}>
                      {selectedOption === opt && <span style={{ marginRight: 8 }}>✓</span>}{opt}
                      {decision.option_costs && decision.option_costs[opt] != null && (
                        <span style={{ color: selectedOption === opt ? B.goldlt : '#8A8A8A', fontSize: 12, marginLeft: 8 }}>· ~${Number(decision.option_costs[opt]).toLocaleString()}</span>
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <textarea placeholder="Type the decision here…" value={answer} onChange={e => setAnswer(e.target.value)} rows={4}
                  style={{ width: '100%', background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 9, padding: '12px 14px', color: B.white, fontSize: 14, outline: 'none', fontFamily: FONT, boxSizing: 'border-box', lineHeight: 1.5, resize: 'vertical' }} autoFocus />
              )}
            </div>

            {/* Photo upload — both builder and client */}
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Photos</label>
              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => photoRef.current?.click()}
                style={{ border: `2px dashed ${dragOver ? B.goldlt : B.slate}`, borderRadius: 12, padding: '16px', cursor: 'pointer', background: dragOver ? 'rgba(248,218,107,0.06)' : B.mid, marginBottom: 10, textAlign: 'center', transition: 'all 0.15s' }}
              >
                <div style={{ fontSize: 24, marginBottom: 4 }}>📷</div>
                <div style={{ color: B.muted, fontSize: 12 }}>Tap to select, drag & drop, or paste</div>
                <input ref={photoRef} type="file" accept="image/*" multiple onChange={e => handlePhotoSelect(e.target.files)} style={{ display: 'none' }} />
              </div>
              {photos.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {photos.map((p, i) => (
                    <div key={i} style={{ position: 'relative' }}>
                      <img src={p.preview} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: `1px solid ${B.slate}` }} />
                      <button onClick={() => setPhotos(prev => prev.filter((_, idx) => idx !== i))}
                        style={{ position: 'absolute', top: -6, right: -6, background: '#E74C3C', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', color: '#fff', fontSize: 12, lineHeight: '20px', textAlign: 'center', padding: 0 }}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button onClick={handleFinalDecision} disabled={saving || uploadingPhoto || (isOptions ? !selectedOption : !answer.trim())}
              style={{ width: '100%', background: (isOptions ? selectedOption : answer.trim()) ? '#4CAF7D' : B.slate, color: (isOptions ? selectedOption : answer.trim()) ? '#fff' : B.muted, border: 'none', borderRadius: 13, padding: 16, cursor: (isOptions ? selectedOption : answer.trim()) ? 'pointer' : 'default', fontSize: 15, fontWeight: 800, fontFamily: FONT }}>
              {saving || uploadingPhoto ? 'Saving…' : isBuilder ? '✓ SEND CONFIRMATION' : '✓ FINAL DECISION'}
            </button>
          </>
        )}

        {isAwaitingConfirm && (
          <div style={{ background: 'rgba(248,218,107,0.07)', border: `1px solid ${B.gold}`, borderRadius: 12, padding: 16, marginTop: 16 }}>
            <div style={{ color: B.goldlt, fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>
              {isBuilder ? '✋ Sent for client confirmation' : '✋ Dan entered this decision — please confirm'}
            </div>
            <div style={{ color: B.white, fontSize: 15, fontWeight: 700, lineHeight: 1.5 }}>{decision.answer}</div>
            <div style={{ color: B.muted, fontSize: 10, marginTop: 6 }}>
              Entered by {decision.decided_by_name || 'Dan'}{decision.completed_at ? ` · ${new Date(decision.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${new Date(decision.completed_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : ''}
            </div>
            {!isBuilder && (
              <button onClick={handleConfirm} disabled={saving}
                style={{ width: '100%', marginTop: 12, background: '#4CAF7D', color: '#fff', border: 'none', borderRadius: 11, padding: 14, cursor: 'pointer', fontSize: 14, fontWeight: 800, fontFamily: FONT }}>
                {saving ? 'Confirming…' : '✓ Confirm This Decision'}
              </button>
            )}
          </div>
        )}

        {isCompleted && decision.confirmed_by_name && (
          <div style={{ color: '#4CAF7D', fontSize: 11, fontWeight: 700, marginTop: 10 }}>
            ✓ Confirmed by {decision.confirmed_by_name}{decision.confirmed_at ? ` · ${new Date(decision.confirmed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${new Date(decision.confirmed_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : ''}
          </div>
        )}

        {isBuilder && (isCompleted || isAwaitingConfirm) && (
          <button onClick={handleReopen} disabled={saving}
            style={{ width: '100%', marginTop: 14, background: 'transparent', border: '1px solid #E67E22', borderRadius: 11, padding: 12, cursor: 'pointer', color: '#E67E22', fontSize: 13, fontWeight: 800, fontFamily: FONT }}>
            {saving ? 'Reopening…' : '🔄 Reopen — Move Back to Needed'}
          </button>
        )}

        {/* ── Comments thread ── */}
        <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${B.slate}` }}>
          <DecisionAttachments decision={decision} />

          <div style={{ color: B.goldlt, fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 10 }}>
            💬 Comments{comments.length > 0 ? ` (${comments.length})` : ''}
          </div>
          {comments.map(c => {
            const mine = (isBuilder && c.author_role === 'builder') || (!isBuilder && c.author_role === 'client');
            return (
              <div key={c.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                <div style={{ maxWidth: '85%', background: mine ? 'rgba(248,218,107,0.1)' : B.mid, border: `1px solid ${mine ? B.gold : B.slate}`, borderRadius: 12, padding: '9px 13px' }}>
                  <div style={{ color: mine ? B.goldlt : B.muted, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>
                    {c.author_role === 'builder' ? 'Dan' : 'Client'} · {new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} {new Date(c.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </div>
                  <div style={{ color: B.white, fontSize: 13, lineHeight: 1.5 }}>{c.content}</div>
                </div>
              </div>
            );
          })}
          <div style={{ display: 'flex', gap: 8, marginTop: comments.length ? 10 : 0 }}>
            <input value={commentText} onChange={e => setCommentText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') sendComment(); }}
              placeholder="Add a comment…"
              style={{ flex: 1, background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 10, padding: '11px 13px', color: B.white, fontSize: 13, outline: 'none', fontFamily: FONT, boxSizing: 'border-box' }} />
            <button onClick={sendComment} disabled={sendingComment || !commentText.trim()}
              style={{ background: commentText.trim() ? B.goldlt : B.slate, color: commentText.trim() ? B.black : B.muted, border: 'none', borderRadius: 10, padding: '11px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 800, fontFamily: FONT }}>
              {sendingComment ? '…' : 'Send'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );

}


function DecisionAttachments({ decision }) {
  const [uploading, setUploading] = useState(false);
  const [dragHover, setDragHover] = useState(false);
  const [list, setList] = useState(Array.isArray(decision.attachments) ? decision.attachments : []);

  useEffect(() => {
    const onPaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            const ext = (file.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
            addFile(new File([file], `pasted_photo_${Date.now()}.${ext}`, { type: file.type }));
          }
        }
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  });

  const addFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const path = `files/decisions_${decision.id}_${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const { error } = await supabase.storage.from('purchase-photos').upload(path, file);
      if (!error) {
        const { data } = supabase.storage.from('purchase-photos').getPublicUrl(path);
        const next = [...list, { name: file.name, url: data.publicUrl }];
        await supabase.from('decisions').update({ attachments: next }).eq('id', decision.id);
        setList(next);
      }
    } catch(e) { console.error('Attachment upload failed:', e); }
    setUploading(false);
  };

  const removeFile = async (i) => {
    const next = list.filter((_, idx) => idx !== i);
    await supabase.from('decisions').update({ attachments: next.length ? next : null }).eq('id', decision.id);
    setList(next);
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ color: B.muted, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>📎 Attachments{list.length > 0 ? ` (${list.length})` : ''}</div>
      {list.map((a, i) => {
        const isImg = /\.(png|jpe?g|gif|webp|heic)($|\?)/i.test(a.url) || /\.(png|jpe?g|gif|webp|heic)$/i.test(a.name || '');
        return (
        <div key={i} style={{ background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 9, padding: '8px 11px', marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13 }}>{isImg ? '🖼' : '📎'}</span>
            <a href={a.url} target="_blank" rel="noreferrer"
              style={{ flex: 1, color: '#4FA3E0', fontSize: 12, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {a.name}
            </a>
            <button onClick={() => removeFile(i)}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: B.muted, fontSize: 14, padding: 0 }}>×</button>
          </div>
          {isImg && (
            <a href={a.url} target="_blank" rel="noreferrer">
              <img src={a.url} alt="" style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 8, marginTop: 8, display: 'block' }} />
            </a>
          )}
        </div>
        );
      })}
      <label
        onDragOver={e => { e.preventDefault(); setDragHover(true); }}
        onDragLeave={() => setDragHover(false)}
        onDrop={e => { e.preventDefault(); setDragHover(false); Array.from(e.dataTransfer.files || []).forEach(fl => addFile(fl)); }}
        style={{ display: 'block', border: `1px dashed ${dragHover ? B.goldlt : B.slate}`, background: dragHover ? 'rgba(248,218,107,0.06)' : 'transparent', borderRadius: 10, padding: '9px 14px', cursor: 'pointer', color: dragHover ? B.goldlt : B.muted, fontSize: 12, textAlign: 'center' }}>
        📎 {uploading ? 'Uploading…' : 'Attach a file — drag & drop, paste, or tap'}
        <input type="file" multiple style={{ display: 'none' }}
          onChange={e => { Array.from(e.target.files || []).forEach(fl => addFile(fl)); e.target.value = ''; }} />
      </label>
    </div>
  );
}
